
import { GoogleGenAI, Type } from "@google/genai";
import { AIRecommendation, Category, WizardFormData, Professional } from "../types";

export interface NewsItem {
  title: string;
  url: string;
  source: string;
  summary: string;
}

// Initialize the Gemini client - fresh instance for each call per guidelines
const getAi = () => new GoogleGenAI({ apiKey: process.env.API_KEY! });

/**
 * Normalizes a URL to ensure it has a protocol and is clean for the AI tools
 */
const normalizeUrl = (url: string) => {
  let trimmed = url.trim();
  if (!trimmed) return trimmed;
  trimmed = trimmed.replace(/\/+$/, "");
  if (!/^https?:\/\//i.test(trimmed)) {
    trimmed = 'https://' + trimmed;
  }
  return trimmed;
};

/**
 * AI Autofill: Researches a website URL to extract business details for a listing.
 */
export const autofillListingFromUrl = async (url: string): Promise<Partial<Professional>> => {
  try {
    const ai = getAi();
    const categories = Object.values(Category).join(", ");
    const targetUrl = normalizeUrl(url);
    
    const prompt = `
      TASK: Research the Australian business at "${targetUrl}" and provide professional directory data.
      INSTRUCTIONS:
      1. Use Google Search to find official business name, ABN, and contact details.
      2. Identify the core discipline from: [${categories}].
      3. CRITICAL: Extract the FULL PHYSICAL STREET ADDRESS (including Building Name, Level, Suite number if applicable).
      4. Find URLs for logo and project images.
      5. Generate a 2-paragraph bio.
      Populate only what you can find. Return as valid JSON.
    `;

    const response = await ai.models.generateContent({
      model: "gemini-3-pro-preview",
      contents: prompt,
      config: {
        systemInstruction: "You are the PlanStack Onboarding Specialist. You research Australian AEC firms. You use googleSearch for verified facts. You prioritize finding the exact physical street address including unit/level details. Permissive-first approach.",
        tools: [{ googleSearch: {} }],
        thinkingConfig: { thinkingBudget: 4000 },
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            businessName: { type: Type.STRING },
            category: { type: Type.STRING },
            description: { type: Type.STRING },
            abn: { type: Type.STRING },
            location: {
              type: Type.OBJECT,
              properties: {
                suburb: { type: Type.STRING },
                state: { type: Type.STRING },
                address: { type: Type.STRING, description: "Full street address including suite/level if found" }
              }
            },
            contact: {
              type: Type.OBJECT,
              properties: {
                phone: { type: Type.STRING },
                email: { type: Type.STRING },
                website: { type: Type.STRING }
              }
            },
            tags: { type: Type.ARRAY, items: { type: Type.STRING } },
            logoUrl: { type: Type.STRING },
            imageUrl: { type: Type.STRING }
          },
          required: ["businessName"]
        }
      }
    });

    const resultText = response.text || "{}";
    const jsonMatch = resultText.match(/\{[\s\S]*\}/);
    const result = JSON.parse(jsonMatch ? jsonMatch[0] : resultText);
    
    if (result.category) {
      const match = Object.values(Category).find(c => 
        c.toLowerCase().includes(result.category.toLowerCase()) ||
        result.category.toLowerCase().includes(c.toLowerCase())
      );
      result.category = match || Category.TOWN_PLANNING;
    }

    return result;
  } catch (error: any) {
    console.error("AI Autofill Failure:", error);
    throw new Error(`PlanStack AI couldn't reliably scrape "${url}". Please fill details manually.`);
  }
};

/**
 * HIGH-INTELLIGENCE WIZARD: Analyzes property proposals with deep reasoning and live search.
 */
export const getProjectRecommendations = async (data: WizardFormData): Promise<AIRecommendation> => {
  try {
    const ai = getAi();
    
    const prompt = `
      STRATEGIC ANALYSIS REQUEST:
      SITE: ${data.address}
      PROJECT: ${data.projectType}
      BUDGET: ${data.budget}
      CLIENT OBJECTIVES: ${data.description}

      YOUR TASK:
      1. Use Google Search to look up the specific council and planning scheme for this address.
      2. Identify high-probability zones (e.g. GRZ, MUZ, R3) and overlays (e.g. HO, BMO, SLO, VPO).
      3. Cross-reference against standard state policies (e.g. ResCode Clause 54/55 in VIC, ADG in NSW).
      4. List mandatory technical reports (e.g. Town Planning Report, Clause 55 Assessment, Land Survey).
      5. List the consultant team required.
      6. Estimate a realistic professional fee range for the planning/permit stage only.

      CRITICAL LOGIC: If you identify that a "Town Planning Report", "Clause 55 Report", or "Clause 54 Report" is required, you MUST include a "Town Planning" consultant in the coreRoles.

      OUTPUT REQUIREMENT:
      - Valid JSON only. 
      - Summary must be a high-level strategic professional opinion.
      - coreRoles MUST map exactly to the Category enum values.
    `;

    const response = await ai.models.generateContent({
      model: "gemini-3-pro-preview",
      contents: prompt,
      config: {
        systemInstruction: `You are the PlanStack Core Intelligence Engine. You are a Senior Development Strategist with deep expertise in Australian planning law (VPP, NSW EP&A Act). 
        You provide high-accuracy, site-specific strategies. 
        Always consider site constraints like slope, heritage, and vegetation based on the address provided. 
        If a Town Planning report or Clause 54/55 report is required, a Town Planning Consultant is mandatory.
        Tone: Professional, authoritative, and strategic.`,
        tools: [{ googleSearch: {} }],
        thinkingConfig: { thinkingBudget: 32768 },
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            summary: { type: Type.STRING },
            projectScope: { type: Type.STRING, description: "Detailed roadmap steps separated by pipe |" },
            coreRoles: { type: Type.ARRAY, items: { type: Type.STRING } },
            additionalRoles: { type: Type.ARRAY, items: { type: Type.STRING } },
            potentialOverlays: { type: Type.ARRAY, items: { type: Type.STRING } },
            requiredReports: { type: Type.ARRAY, items: { type: Type.STRING } },
            estimatedFeeRange: { type: Type.STRING }
          },
          required: ["summary", "projectScope", "coreRoles", "potentialOverlays", "requiredReports", "estimatedFeeRange"]
        }
      }
    });

    const result = JSON.parse(response.text || "{}");
    
    // Safety check: ensure categories match our enum
    const validCategories = Object.values(Category);
    result.coreRoles = (result.coreRoles || [])
      .map((r: string) => {
        const match = validCategories.find(c => c.toLowerCase() === r.toLowerCase() || c.toLowerCase().includes(r.toLowerCase()));
        return match || null;
      })
      .filter(Boolean) as Category[];
    
    // Logic: Force-include Town Planning if specific reports are requested
    const planningKeywords = ["planning report", "clause 55", "clause 54", "planning assessment", "town planning"];
    const reportsStr = (result.requiredReports || []).join(" ").toLowerCase();
    const needsPlanner = planningKeywords.some(kw => reportsStr.includes(kw));
    
    if (needsPlanner && !result.coreRoles.includes(Category.TOWN_PLANNING)) {
      result.coreRoles.unshift(Category.TOWN_PLANNING);
    }

    // Force-include Builder for development feasibility if missing
    if (!result.coreRoles.includes(Category.BUILDER)) {
      result.coreRoles.push(Category.BUILDER);
    }
    
    return result;
  } catch (error) {
    console.error("High-Intelligence Analysis Error:", error);
    return {
      summary: "Standard Australian project requirements apply. A formal site assessment is recommended.",
      projectScope: "Land Survey | Concept Design | Pre-Application Meeting | Planning Submission | Building Permit",
      coreRoles: [
        Category.TOWN_PLANNING,
        Category.DRAFTING_DESIGN,
        Category.LAND_SURVEYING,
        Category.BUILDER
      ],
      additionalRoles: [Category.ENGINEERING_CIVIL],
      potentialOverlays: ["Varies by Council"],
      requiredReports: ["Title Search", "Site Feature Survey", "Town Planning Report"],
      estimatedFeeRange: "$15,000 - $25,000"
    };
  }
};

export const getPlanningQuickAdvice = async (question: string, context?: string): Promise<string> => {
  try {
    const ai = getAi();
    const response = await ai.models.generateContent({
      model: "gemini-3-flash-preview",
      contents: question,
      config: {
        systemInstruction: "You are the PlanStack Concierge. Answer building and planning questions with 100% Australian context. Always link your advice back to the need for professional consultants. End with: 'Consult the PlanStack directory to find a vetted specialist for your specific site.'",
      }
    });
    return response.text || "I'm sorry, I couldn't process that.";
  } catch (e) {
    console.error("Advice error:", e);
    return "The AI assistant is having trouble connecting to the planning engine. Please try again later.";
  }
};

export const getRecentPlanningNews = async (): Promise<NewsItem[]> => {
  try {
    const ai = getAi();
    const response = await ai.models.generateContent({
      model: "gemini-3-flash-preview",
      contents: "Find 3 recent property development or planning news items in Australia.",
      config: {
        tools: [{ googleSearch: {} }],
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.ARRAY,
          items: {
            type: Type.OBJECT,
            properties: {
              title: { type: Type.STRING },
              url: { type: Type.STRING },
              source: { type: Type.STRING },
              summary: { type: Type.STRING }
            }
          }
        }
      }
    });

    const parsedNews = JSON.parse(response.text || "[]");
    const chunks = response.candidates?.[0]?.groundingMetadata?.groundingChunks;
    if (chunks && Array.isArray(chunks)) {
      parsedNews.forEach((item: NewsItem, idx: number) => {
        const chunk = chunks[idx];
        if (chunk?.web?.uri) {
          item.url = chunk.web.uri;
        }
      });
    }

    return parsedNews;
  } catch (error) {
    console.error("News Fetch Error:", error);
    return [];
  }
};
